import moment from 'moment';
import { useMemo } from 'react';
import { Project } from '../../domain/project/Project';
import { VehicleFlat } from '../../domain/vehicle/VehicleFlat';
import { BinTypes, ChartTypes } from '../../interfaces/analytics';
import { useRouterQuery } from '../useRouterQuery';
import { useRetrieveFlatVehicles } from '../vehicle/useRetrieveFlatVehicles';

interface UseAnalyticsFiltersProps {
  project?: Project;
}

export interface UseAnalyticsFiltersResponse {
  binType: BinTypes;
  chartType: ChartTypes;
  startDate: Date;
  endDate: Date;
  stackedChart: boolean;
  cumulativeChart: boolean;
  vehicles: Array<VehicleFlat>;
}

export const useAnalyticsFilters = ({ project }: UseAnalyticsFiltersProps): UseAnalyticsFiltersResponse => {
  const urlParams = useRouterQuery();
  const { data: projectVehicles } = useRetrieveFlatVehicles({ project });
  const chartType = (urlParams.get(`chart_type`) ?? `line`) as ChartTypes;

  const stackedChartParam = urlParams.get(`stacked_chart`);
  const stackedChart = stackedChartParam ? stackedChartParam === `true` : false;

  const cumulativeChartParam = urlParams.get(`cumulative_chart`);
  const cumulativeChart = cumulativeChartParam ? cumulativeChartParam === `true` : false;

  const startDateParam = urlParams.get(`start_date`);
  const startDate = useMemo(
    () => (startDateParam ? new Date(startDateParam) : moment(new Date()).subtract(`1`, `month`).toDate()),
    [startDateParam]
  );
  const endDateParam = urlParams.get(`end_date`);
  const endDate = useMemo(() => (endDateParam ? new Date(endDateParam) : new Date()), [endDateParam]);

  const vehicleIdsParam = urlParams.getAll(`vehicle_ids`);

  const vehicleIds = useMemo(() => (vehicleIdsParam ?? []) as Array<string>, [vehicleIdsParam]);

  const vehicles = useMemo(
    () => (projectVehicles ?? [])?.filter((vehicle) => vehicleIds.includes(vehicle.id)),
    [projectVehicles, vehicleIds]
  );

  const binType = (urlParams.get(`bin_type`) ?? `day`) as BinTypes;

  return {
    binType,
    chartType,
    stackedChart,
    cumulativeChart,
    startDate,
    endDate,
    vehicles,
  };
};
